import { WorkflowConfigViewModel } from '@ail/orders-api'
import { useCallback, useMemo } from 'react'
import { NavLink } from 'react-router-dom'
import { downloadFile } from '../../../helpers/FileDownload'
import { useGetApprovalProcessConfigsQuery, useGetWorkflowConfigsQuery } from '../../../redux/api/ordersApi'
import { Grid, GridColumn } from '../../common/Grid/Grid'
import './SupplierWorkflows.scss'
import { Loader } from '../../common/Loader/Loader'

interface GridRow extends WorkflowConfigViewModel {
  tenantName?: string
}
export default function SupplierWorkflowsFunctional() {
  const approvals = useGetApprovalProcessConfigsQuery()
  const supplierWorkflows = useGetWorkflowConfigsQuery()

  const downloadJson = useCallback((form: GridRow) => {
    const type = 'application/json'
    const file = new Blob([JSON.stringify(form)], { type })
    const fileURL = URL.createObjectURL(file)
    const filename = `${form.name}.json`

    downloadFile({ fileURL, filename })
  }, [])

  // #region FilterFns
  const filterFns = useMemo(() => {
    return {
      byGlobalString: (workflow: GridRow, value: string) => {
        const splitQuery = value.trim().toLowerCase().split(/\s+/).filter(Boolean)
        if (splitQuery.length === 0) return true

        const targetString = [workflow.name, workflow.id].filter(Boolean).join(' ').toLowerCase()

        return splitQuery.every((q) => targetString.includes(q))
      },
      byWorkflowId: (workflow: GridRow, value: string) => {
        const val = value.trim().toLowerCase()
        return workflow.id.toLowerCase().includes(val)
      },
      byName: (workfloww: GridRow, value: string) => {
        const splitQuery = value.trim().toLowerCase().split(/\s+/).filter(Boolean)
        if (splitQuery.length === 0) return true

        const fullName = `${workfloww.name || ''}`.toLowerCase()

        return splitQuery.every((q) => fullName.includes(q))
      },
    }
  }, [])
  // #endregion

  // #region DataRows
  const dataRows: GridRow[] = useMemo(() => {
    if (!supplierWorkflows.data) return []

    return supplierWorkflows.data.map((r) => {
      const row: GridRow = {
        ...r,
      }
      return row
    })
  }, [supplierWorkflows.data])
  // #endregion

  // #region Columns
  const columns: GridColumn<GridRow>[] = useMemo(
    () => [
      {
        key: 'id',
        name: 'Workflow ID',
        width: 350,
        cellClass: 'id',
        renderCell: ({ row }) => <div>{row.id}</div>,
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.byWorkflowId,
          customPlaceholder: 'Search by workflow ID',
        },
      },
      {
        key: 'type',
        name: 'Workflow name',
        maxWidth: 500,
        width: 400,
        cellClass: 'type',
        renderCell: ({ row }) => <div>{row.name}</div>,
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.byName,
          customPlaceholder: 'Search by workflow name',
        },
      },
      {
        key: 'orders',
        name: 'Supplier / internal',
        width: 150,
        cellClass: 'amount',
        renderCell: ({ row }) => <div>{row.orders?.length ?? 0}</div>,
        filter: {
          position: 'header-right',
          type: 'inline_text_filter',
          filterFn: filterFns.byGlobalString,
          customPlaceholder: 'Global search',
        },
      },
      {
        key: 'actions',
        name: 'Actions',
        width: 150,
        renderCell: ({ row }) => (
          <div className="actions">
            <NavLink to={`/RequestsManagement/EditSupplierWorkflow/${row.id}`}>
              <i className="icon-edit" />
            </NavLink>
            <a onClick={() => downloadJson(row)}>
              <i className="icon-download" />
            </a>
          </div>
        ),
      },
    ],
    [filterFns.byWorkflowId, filterFns.byName, filterFns.byGlobalString, downloadJson]
  )
  // #endregion

  // #region Render
  return (
    <Loader blocking={approvals.isFetching} className="grid-size">
      <Grid
        id="SupplierWorkflowsAdminPanel"
        columns={columns}
        rows={dataRows}
        rowKeyGetter={(r) => r.id}
        title="Workflow List"
        icon="icon-requests"
        customClass="adminPanelGrid supplierWorkflows"
        renderCustomOption={() => {
          return [
            <NavLink
              className={'grid-options-widget'}
              key={'newWorkflow'}
              to="/RequestsManagement/EditSupplierWorkflow/new"
            >
              New Workflow
            </NavLink>,
            <NavLink className={'grid-options-widget'} key={'formsList'} to="/RequestsManagement">
              Requests Management
            </NavLink>,
          ]
        }}
        hideReorder
        hideExport
      />
    </Loader>
  )
}
